import { useAuthContext } from '../context/AuthContext'
import { useState, useEffect } from 'react'
import toast, { Toaster } from 'react-hot-toast'

import './Quote.css'

// Function to set page orientation for printing
const setPageOrientation = (orientation: 'portrait' | 'landscape') => {
   // Remove any existing page orientation styles
   const existingStyle = document.getElementById('dynamic-page-orientation')
   if (existingStyle) {
      existingStyle.remove()
   }

   // Create new style element with the correct orientation
   const style = document.createElement('style')
   style.id = 'dynamic-page-orientation'
   style.textContent = `
    @page {
      size: Letter ${orientation};
      margin: ${orientation === 'landscape' ? '.25in .25in 0 .25in' : '.25in .25in .25in .25in'};
      padding: 0;
    }
  `
   document.head.appendChild(style)
}
import QuoteOptionHeader from './QuoteOptionHeader.js'
import generatePDF from './PDF.js'
import LoadingScreen from '../components/rows/misc/LoadingScreen.jsx'
import UnauthorizedScreen from '../components/rows/misc/UnauthorizedScreen.jsx'
import { useSearchParams } from 'react-router-dom'

// Javascript Files
import QuoteData from './QuoteData.jsx'
import FrontPage from './FrontPage.js'
import useLocalization from '../hooks/useLocalization.js'
import { useTime } from '../hooks/queries/onMountQueries'
import QuoteContent from './QuoteContent.js'

export function QuotePreview() {
   const host_url = process.env.REACT_APP_HOST_URL
   // Server-provided time (same source as timeData in the quote context)
   const { data: timeData } = useTime()
   // Formatted HTML React App
   const [formType, updateFormType] = useState(1)
   const [searchParams, updateSearchParams] = useSearchParams()
   const [invoiceNumber, updateInvoiceNumber] = useState('')
   const [featurePermissions, setFeaturePermissions] = useState(null)
   const [allowPrepaymentInvoice, setAllowPrepaymentInvoice] = useState(false)
   const [opportunity_id, setopportunity_id] = useState(
      searchParams.get('opportunity_id'),
   )

   // Set landscape orientation when component mounts
   useEffect(() => {
      setPageOrientation('landscape')
   }, [])

   useEffect(() => {
      if (
         searchParams.get('invoice_number') &&
         searchParams.get('invoice_number') !== 'undefined'
      ) {
         updateInvoiceNumber(searchParams.get('invoice_number') as string)
      } else {
         const today = new Date()
         const invoice_number = `10${today.getFullYear() % 10}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`
         updateInvoiceNumber(invoice_number)
      }
   }, [])
   useEffect(() => {
      async function getFeaturePermissions() {
         const response = await fetch(`${host_url}/api/feature-permissions`)
         if (response.ok) {
            let data = await response.json()

            if (Object.keys(data).length === 0) {
               toast.error(`Error Retrieving Feature Permissions`, {
                  duration: 5000,
               })
               return null
            }
            return data
         }
      }
      if (!featurePermissions) {
         getFeaturePermissions().then((data) => {
            setFeaturePermissions(data)
         })
      }
   }, [])
   useEffect(() => {
      if (featurePermissions) {
         const PrepaymentInvoicePermissions: string[] =
            'PrepaymentInvoiceQuoteType' in featurePermissions
               ? featurePermissions['PrepaymentInvoiceQuoteType']
               : []
         if (
            (PrepaymentInvoicePermissions &&
               PrepaymentInvoicePermissions.includes(user_role)) ||
            user_role === 'Super Admin'
         ) {
            setAllowPrepaymentInvoice(true)
         }
      }
   }, [featurePermissions])

   //TODO: fix this once we have a real TS QuoteData
   const [PDFQuoteData, setPDFQuoteData] = useState(null as any)
   const [initialized, setInitialized] = useState(false)

   useEffect(() => {
      ;(async () => {
         const mainOpp = new QuoteData(opportunity_id, user_role)
         let initialized = await mainOpp.initializeQuoteData()
         //TODO: make this idempotent await mainOpp.initialize();
         setPDFQuoteData(mainOpp.GeneratedQuoteData)
         setInitialized(initialized)

         // Check if quote is booked and set formType to 4
         if (
            mainOpp.GeneratedQuoteData &&
            (mainOpp.GeneratedQuoteData as any).Stage === 'booked'
         ) {
            updateFormType(4)
         }
      })()
   }, [opportunity_id])

   const [monthlyTotal, updateMonthlyCost] = useState(0)
   const [dueAtActivation, updateDueAtActivation] = useState(0)
   const [calculatedValues, updateCalculatedValues] = useState(null)
   // Get api auth status
   const { authStatus } = useAuthContext()
   // list of quote user roles
   const user_roles = [
      { value: 'Admin', name: 'Admin' },
      { value: 'Sales', name: 'Sales' },
      { value: 'Expansion', name: 'Expansion' },
      {
         value: 'Expansion Ongoing Services',
         name: 'Expansion Ongoing Services',
      },
      { value: 'Reseller', name: 'Reseller' },
      { value: 'Partner', name: 'Partner' },
      { value: 'Payroll', name: 'Payroll' },
      { value: 'Sales Manager', name: 'Sales Manager' },
      { value: 'Expansion Manager', name: 'Expansion Manager' },
      {
         value: 'Expansion Manager Ongoing Services',
         name: 'Expansion Manager Ongoing Services',
      },
      { value: 'EMEA Sales', name: 'EMEA Sales' },
      { value: 'EMEA Expansion', name: 'EMEA Expansion' },
      { value: 'EMEA Sales Manager', name: 'EMEA Sales Manager' },
      { value: 'EMEA Expansion Manager', name: 'EMEA Expansion Manager' },
   ]
   // list sf user roles
   const [user_role, setuser_role] = useState(() => {
      // Not the best way to redirect. Needs replaced.
      let selected_user_role = searchParams.get('agent_role')
      // Checks is user_view is set. If not, return "Internal"
      if (selected_user_role != null) return selected_user_role
      else if (
         document.referrer.includes('force.com') ||
         document.referrer.includes('quote.bamboohr.net')
      ) {
         return 'Sales'
      } else return 'Partner'
   })

   function refreshPage() {
      window.location.reload()
   }

   if (isNaN(authStatus) || !initialized) return <LoadingScreen />
   // If API call authorized, display quote pdf.
   else if (authStatus == 200 && initialized) {
      return (
         <div className='newquote-wrapper'>
            <Toaster
               position='top-center'
               toastOptions={{
                  style: {
                     wordBreak: 'break-all',
                     maxWidth: '25%',
                     minWidth: '25%',
                  },
               }}
            />
            <QuoteOptionHeader
               formType={formType}
               opportunity_id={searchParams.get('opportunity_id') as string}
               account_name={searchParams.get('account_name') as string}
               opportunity_name={searchParams.get('opportunity_name') as string}
               opportunity_contact_name={
                  searchParams.get('opportunity_contact_name') as string
               }
               opportunity_contact_email={
                  searchParams.get('opportunity_contact_email') as string
               }
               last_agent_email={searchParams.get('last_agent_email') as string}
               last_agent_name={searchParams.get('last_agent_name') as string}
               onTypeChangeCallback={updateFormType}
               onGeneratePdfCallback={(...args) =>
                  generatePDF(...args, timeData?.currentDate)
               }
               onRefreshCallback={refreshPage}
               invoice_number={invoiceNumber}
               monthlyGrandTotal={monthlyTotal}
               activationGrandTotal={dueAtActivation}
               calculatedValues={PDFQuoteData.calculatedValues}
               user_role={user_role}
               allowInvoiceQuoteOption={allowPrepaymentInvoice}
               isBooked={
                  PDFQuoteData && (PDFQuoteData as any).Stage === 'booked'
               }
            />
            <PDF
               formType={formType}
               HeaderQuoteData={PDFQuoteData}
               Initialized={initialized}
            />
         </div>
      )
   }
   // If API call unauthorized, display unauthorized error.
   else if (authStatus !== 200 && !isNaN(authStatus)) {
      return <UnauthorizedScreen></UnauthorizedScreen>
   }
}

interface PDFProps {
   formType: number
   HeaderQuoteData?: any
   Initialized?: boolean
}

export function PDF({ formType, HeaderQuoteData, Initialized }: PDFProps) {
   const host_url =
      process.env.REACT_APP_HOST_URL || 'https://quote.bamboohr.net'
   const [searchParams, updateSearchParams] = useSearchParams()

   // Set landscape orientation when component mounts
   useEffect(() => {
      setPageOrientation('landscape')
   }, [])
   // Get api auth status
   const { authStatus } = useAuthContext()

   /////////////////////////////////////
   // Set Strings Variable for Localization
   /////////////////////////////////////
   const {
      strings,
      getString,
      getFormattedString,
      isLoading: isLoadingStrings,
   } = useLocalization(host_url)

   ///////////////////////////////////
   // Set Query Paramters as Variables
   ///////////////////////////////////
   // set query parameters as variables
   const [opportunity_id, setopportunity_id] = useState(
      searchParams.get('opportunity_id'),
   )
   const [account_name, setaccount_name] = useState(
      searchParams.get('account_name')
         ? decodeURIComponent(searchParams.get('account_name') as string)
         : null,
   )
   const [opportunity_name, setopportunity_name] = useState(
      searchParams.get('opportunity_name')
         ? decodeURIComponent(searchParams.get('opportunity_name') as string)
         : null,
   )
   const [opportunity_contact_name, setopportunity_contact_name] = useState(
      searchParams.get('opportunity_contact_name'),
   )
   const [opportunity_contact_email, setopportunity_contact_email] = useState(
      searchParams.get('opportunity_contact_email'),
   )
   const [last_agent_email, setlast_agent_email] = useState(
      searchParams.get('last_agent_email'),
   )
   const [last_agent_name, setlast_agent_name] = useState(
      searchParams.get('last_agent_name'),
   )
   const [requestNumber, setRequestNumber] = useState(searchParams.get(''))
   const [LanguageCode, setLanguageCode] = useState('en-US')
   useEffect(() => {
      if (
         searchParams.get('invoice_number') &&
         searchParams.get('invoice_number') !== 'undefined'
      ) {
         setRequestNumber(searchParams.get('invoice_number'))
      } else {
         const today = new Date()
         const invoice_number = `10${today.getFullYear() % 10}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`
         setRequestNumber(invoice_number)
      }
   }, [])
   // Initialize formType from either the prop or URL parameter
   const formTypeFromUrl = searchParams.get('form_type')
      ? Number(searchParams.get('form_type'))
      : null
   const [PDFFormType, setPDFFormType] = useState(
      formType || formTypeFromUrl || 1,
   )
   const [PDFUserRole, setPDFUserRole] = useState(
      searchParams.get('agent_role'),
   )

   // Update PDFFormType when formType prop changes (from QuoteOptionHeader)
   useEffect(() => {
      if (formType) {
         setPDFFormType(formType)
      }
   }, [formType])

   useEffect(() => {
      if (PDFUserRole) setPDFUserRole(PDFUserRole)
   }, [PDFUserRole])

   //TODO: fix this once we have a real TS QuoteData
   const [PDFQuoteData, setPDFQuoteData] = useState(
      HeaderQuoteData || (null as any),
   )
   const [initialized, setInitialized] = useState(Initialized || false)

   useEffect(() => {
      ;(async () => {
         if (initialized) return
         else {
            const mainOpp = new QuoteData(opportunity_id, PDFUserRole)
            let thisInitialized = await mainOpp.initializeQuoteData()
            //TODO: make this idempotent await mainOpp.initialize();
            setPDFQuoteData(mainOpp.GeneratedQuoteData)
            setInitialized(thisInitialized)
         }
      })()
   }, [opportunity_id, initialized])

   // If no products, then show toast popup.
   useEffect(() => {
      if (
         initialized &&
         PDFQuoteData &&
         !PDFQuoteData?.HasOneTimeServices &&
         !PDFQuoteData?.HasMonthlyServices
      ) {
         toast(
            'It seems there are no products to display in this quote.\nPlease check that products have been added and the correct quote type selected (if applicable).',
            { duration: 5000, icon: '⚠️' },
         )
      }
   }, [initialized, PDFQuoteData])

   if (isNaN(authStatus)) return <LoadingScreen />
   // If API call authorized, display quote pdf.
   else if (initialized && authStatus == 200) {
      return (
         <div className='newquote-wrapper'>
            <Toaster
               position='top-center'
               toastOptions={{
                  style: {
                     wordBreak: 'break-all',
                     maxWidth: '25%',
                     minWidth: '25%',
                  },
               }}
            />
            {PDFQuoteData ? (
               <FrontPage
                  getString={(key, fallback) => getString(key, fallback)}
                  ExpirationDate={PDFQuoteData.QuoteExpirationDate}
                  InvoiceNumber={requestNumber as string}
                  formType={PDFFormType as number}
                  AccountName={account_name as string}
                  OppContactName={opportunity_contact_name as string}
                  OppContactEmail={opportunity_contact_email as string}
                  RepName={last_agent_name as string}
                  RepEmail={last_agent_email as string}
                  EECount={PDFQuoteData.maxEECount}
                  FirstYearTotal={PDFQuoteData.FirstYearTotal}
                  OnetimeTotalPrice={PDFQuoteData.OneTimeTotal}
                  MonthlyTotalPrice={PDFQuoteData.MonthlyOngoingTotal}
                  AnnualTotalPrice={PDFQuoteData.AnnualOngoingTotal}
                  PreviousMonthlyTotal={PDFQuoteData.PreviousMonthlyTotal}
                  PreviousAnnualTotal={PDFQuoteData.PreviousAnnualTotal}
                  MonthlyDifference={PDFQuoteData.MonthlyOngoingDifference}
                  AnnualDifference={PDFQuoteData.AnnualDifference}
                  ListPriceTotal={
                     PDFQuoteData.AnnualOngoingTotalListPrice +
                     PDFQuoteData.OneTimeTotalListPrice
                  }
                  TotalDiscount={PDFQuoteData.TotalFirstYearDiscount}
                  DifferenceQuote={PDFQuoteData.DifferenceQuote}
                  ActivationDate={PDFQuoteData.QuoteActivationDate}
                  currencyObject={PDFQuoteData.currencyObject}
                  currencyObjectUSD={PDFQuoteData.currencyObjectUSD}
                  isForeignCurrencyQuote={PDFQuoteData.IsForeignCurrencyQuote}
                  payrollUsdMonthlyTotal={
                     PDFQuoteData.PayrollCombinedTotalPrice
                  }
                  payrollUsdOneTimeTotal={PDFQuoteData.AlwaysUSDOneTimeTotal}
                  payrollUsdPreviousMonthlyTotal={
                     PDFQuoteData.PayrollCombinedPreviousTotalPrice
                  }
                  payrollUsdMonthlyDifference={
                     PDFQuoteData.PayrollCombinedTotalPriceDifference
                  }
                  payrollUsdPreDiscountTotal={
                     PDFQuoteData.PayrollCombinedFirstYearListPrice
                  }
                  payrollUsdFirstYearSavings={
                     PDFQuoteData.PayrollCombinedFirstYearDiscount
                  }
                  payrollUsdFirstYearTotal={
                     PDFQuoteData.PayrollCombinedFirstYearTotalPrice
                  }
                  prepaymentTerm={PDFQuoteData.EstimatedPrepaymentCoverage}
                  termLengthObject={PDFQuoteData.TermLengthObject}
                  HasTimeLimitedDiscount={PDFQuoteData.HasTimeLimitedDiscount}
                  ShowEstimatedCoverage={
                     PDFQuoteData.ShowEstimatedCoveragePeriod
                  }
                  PrepayLength={PDFQuoteData.PrepayLength}
               />
            ) : null}
            <QuoteContent
               getString={getString}
               getFormattedString={getFormattedString}
               PDFQuoteData={PDFQuoteData}
               formType={PDFFormType}
            />
         </div>
      )
   }
   // If API call unauthorized, display unauthorized error.
   else if (authStatus !== 200 && !isNaN(authStatus)) {
      return <UnauthorizedScreen></UnauthorizedScreen>
   } else {
      return <></>
   }
}
